import { Box, Badge } from "@chakra-ui/react";
import Tooltip from "components/Tooltip";
import { FiMail } from "react-icons/fi";

interface UnreadMessageProps {
  /**
   * Whether to hide the mail icon and just show the dot
   */
  hideIcon?: boolean;

  /**
   * Whether to hide the tooltip shown over the unread message indicator
   */
  hideTooltip?: boolean;

  /**
   * The maximum value to display before showing a "max+" indicator (default is 9)
   */
  max?: number;

  /**
   * The size of the icon, default is sm
   */
  size?: "sm" | "md" | "lg";
  /**
   * Current unread message count.
   */
  value: number;

  onClick?: () => void;
}

/**
 * Displays an email icon with a badge showing the number of unread messages.
 * The badge is hidden when `value` is 0.
 */
export const UnreadMessage: React.FC<UnreadMessageProps> = ({
  hideIcon = false,
  hideTooltip = false,
  max = 9,
  size = "md",
  value,
  onClick,
}) => {

  // if no value, we display nothing
  if (isNaN(value) || value <= 0) {
    return null;
  }

  const display = value > max ? `${max}+` : value.toString();
  const right = size === "sm" ? (value > max ? "-17px" : "-10px") : "-8px";
  const sizes = {
    sm: { iconSize: 20, ballSize: 18, fontSize: "text-xs" },
    md: { iconSize: 30, ballSize: 18, fontSize: "text-sm" },
    lg: { iconSize: 40, ballSize: 22, fontSize: "text-base" },
  };

  let { iconSize, ballSize, fontSize } = sizes[size];

  if (hideIcon) {
    ballSize += 2;
    iconSize = 0;
    fontSize = fontSize;
  }

  // span is needed for tooltip positioning
  return (
    <span>
      <Box position="relative" display="inline-flex" onClick={onClick} className={`${onClick ? 'cursor-pointer' : ''}`}>
        <Tooltip label={hideTooltip ? '' : `you have ${display} unread messages`}>
          <span>
            {!hideIcon && <FiMail className="stroke-slate-500 mt-2" size={iconSize} />}
            <div
              className={`absolute top-1 bg-red-500 text-white font-normal flex items-center justify-center rounded-full ${fontSize}`}
              style={{
                height: ballSize,             // fixed height
                minWidth: ballSize,           // ensures single digit is circular
                padding: '0 6px',       // horizontal padding for 2+ digits
                lineHeight: 1,           // ensures vertical centering
                whiteSpace: 'nowrap',    // prevents wrapping for large numbers
                right: right,
                fontSize: fontSize,
              }}
            >
              {display}
            </div>
          </span>
        </Tooltip>
      </Box>
    </span>
  );
};
